import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
	Chip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useRouter } from "utils";
import { STAFF_PATH } from "../actions/types";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: theme.spacing(0, 1.5, 2, 2),
		display: "block",
	},
	list: {
		marginTop: theme.spacing(1),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	row: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		padding: theme.spacing(1.25, 1.5),
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		cursor: "pointer",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	rowMain: {
		minWidth: 0,
		flex: 1,
	},
	name: {
		color: theme.palette.brand,
		fontWeight: 500,
		wordBreak: "break-word",
	},
	meta: {
		color: "#999999",
		marginTop: theme.spacing(0.25),
	},
	chipActive: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
	chipInactive: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
}));

const staffDisplayName = (member) =>
	member.fullName ||
	`${member.firstName || ""} ${member.lastName || ""}`.trim() ||
	"-";

const StaffTeamPanel = ({
	staffId,
	staffList,
	expanded,
	onExpandedChange,
}) => {
	const classes = useStyles();
	const { history } = useRouter();

	const team = useMemo(() => {
		if (!staffId || !staffList?.length) {
			return [];
		}
		return staffList
			.filter((member) => member.supervisor === staffId)
			.sort((a, b) =>
				staffDisplayName(a).localeCompare(staffDisplayName(b), undefined, {
					sensitivity: "base",
				})
			);
	}, [staffId, staffList]);

	if (!staffId || team.length === 0) {
		return null;
	}

	const countLabel = ` (${team.length})`;

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="staff-team-panel"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls="staff-team-panel-content"
				id="staff-team-panel-header"
			>
				<Typography component="h2" className={classes.summaryTitle}>
					Crew
					<span className={classes.countMuted}>{countLabel}</span>
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				<div className={classes.list}>
					{team.map((member) => {
						const isActive =
							String(member.status || "").toLowerCase() !==
							"inactive";
						return (
							<div
								key={member.id}
								className={classes.row}
								onClick={() =>
									history.push(`${STAFF_PATH}/${member.id}`)
								}
								data-cy={`staff-team-member-${member.id}`}
							>
								<div className={classes.rowMain}>
									<Typography
										variant="h5"
										className={classes.name}
									>
										{staffDisplayName(member)}
									</Typography>
									{(member.organisationRole ||
										member.organisationStaffTypesStaffType) && (
										<Typography
											variant="body2"
											className={classes.meta}
										>
											{[
												member.organisationRole,
												member.organisationStaffTypesStaffType,
											]
												.filter(Boolean)
												.join(" · ")}
										</Typography>
									)}
								</div>
								<Chip
									size="small"
									label={isActive ? "Active" : "Inactive"}
									className={
										isActive
											? classes.chipActive
											: classes.chipInactive
									}
								/>
							</div>
						);
					})}
				</div>
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

StaffTeamPanel.propTypes = {
	staffId: PropTypes.string,
	staffList: PropTypes.array,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
};

export default StaffTeamPanel;
